import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

// Define default values for different race types
export const USER_RACE_CONFIG = {
	// Default race (fallback)
	default: {
		name: "Default",
		colorAccent: "#20ade6",
		mean_period_duration: 21 * 24 * 60, // 21 days in minutes
		fertility_window: 3 * 24 * 60, // 3 days in minutes
		temperature_threshold: 39.5,
		activity_threshold: 80,
	},
	// Pig specific values
	pig: {
		name: "Pig",
		colorAccent: "#ff9800",
		mean_period_duration: 21 * 24 * 60, // 21 days in minutes
		fertility_window: 2 * 24 * 60, // 2 days in minutes
		temperature_threshold: 39.2,
		activity_threshold: 75,
	},
	// Cow specific values
	cow: {
		name: "Cow",
		colorAccent: "#4caf50",
		mean_period_duration: 21 * 24 * 60, // 21 days in minutes
		fertility_window: 3 * 24 * 60, // 3 days in minutes
		temperature_threshold: 39.0,
		activity_threshold: 85,
	},
	// Sheep specific values
	sheep: {
		name: "Sheep",
		colorAccent: "#2196f3",
		mean_period_duration: 17 * 24 * 60, // 17 days in minutes
		fertility_window: 2 * 24 * 60, // 2 days in minutes
		temperature_threshold: 39.3,
		activity_threshold: 70,
	},
};

const UserRaceContext = createContext(null);

export function useUserRace() {
	const context = useContext(UserRaceContext);
	if (!context) {
		throw new Error("useUserRace must be used within a UserRaceProvider");
	}
	return context;
}

export function UserRaceProvider({ children }) {
	const { userData } = useAuth();
	const [raceType, setRaceType] = useState("default");
	const [raceConfig, setRaceConfig] = useState(USER_RACE_CONFIG.default);

	useEffect(() => {
		if (userData && userData.Type) {
			const userRaceType = userData.Type.toLowerCase();

			// Set the race type
			setRaceType(userRaceType);

			// Set the race configuration based on the user's type
			// If the type doesn't exist in our config, use default
			if (USER_RACE_CONFIG[userRaceType]) {
				setRaceConfig(USER_RACE_CONFIG[userRaceType]);
			} else {
				setRaceConfig(USER_RACE_CONFIG.default);
			}
		}
	}, [userData]);

	// Value that will be provided to consumers
	const value = {
		raceType,
		raceConfig,
		// Helper functions for commonly used values
		getColorAccent: () => raceConfig.colorAccent,
		getName: () => raceConfig.name,
		getMeanPeriodDuration: () => raceConfig.mean_period_duration,
		getFertilityWindow: () => raceConfig.fertility_window,
		getTemperatureThreshold: () => raceConfig.temperature_threshold,
		getActivityThreshold: () => raceConfig.activity_threshold,
		// Helper to get any configuration value for the current race
		getConfigValue: (key, defaultValue = null) =>
			raceConfig[key] !== undefined ? raceConfig[key] : defaultValue,
	};

	return (
		<UserRaceContext.Provider value={value}>
			{children}
		</UserRaceContext.Provider>
	);
}
